'use client'

import React from 'react'

import { Box, chakra } from '@chakra-ui/react'

import {
  paginationControlsStyles,
  paginationNavButtonStyles,
  paginationPageButtonStyles,
  paginationPerPageLabelStyles,
  paginationPerPageSelectStyles,
  paginationPerPageStyles,
  paginationRangeStyles,
  paginationStyles,
} from './Table.styles'
import { useTableColorScheme, useTableDensity } from './TableContext'

export type TablePaginationProps = {
  /** Total number of items across all pages. */
  total: number
  /** Zero-based index of the current page. */
  pageIndex: number
  /** Number of items displayed per page. */
  pageSize: number
  /** Total number of pages. */
  pageCount: number
  /** Called with the new zero-based page index when a page control is clicked. */
  onPageChange: (pageIndex: number) => void
  /** Called with the new page size when the per-page selector changes. */
  onPageSizeChange?: (pageSize: number) => void
  /** Options available in the per-page selector. Defaults to [10, 25, 50]. */
  pageSizeOptions?: number[]
  /** Label shown before the per-page selector, e.g. "Devices per page:". */
  pageSizeLabel?: string
  /**
   * Center the page controls in the footer. Without a per-page selector the
   * controls otherwise sit at the right edge (range left, controls right);
   * this balances the range with an equal spacer so the controls sit centered.
   */
  centerControls?: boolean
}

const MAX_PAGE_BUTTONS = 5

/** Returns the page numbers to show, inserting null for ellipsis gaps. */
function getPageNumbers(
  pageIndex: number,
  pageCount: number
): (number | null)[] {
  if (pageCount <= MAX_PAGE_BUTTONS) {
    return Array.from({ length: pageCount }, (_, i) => i)
  }

  const pages: (number | null)[] = []

  // Always show first page
  pages.push(0)

  const windowStart = Math.max(1, pageIndex - 1)
  const windowEnd = Math.min(pageCount - 2, pageIndex + 1)

  if (windowStart > 1) pages.push(null) // leading ellipsis

  for (let i = windowStart; i <= windowEnd; i++) {
    pages.push(i)
  }

  if (windowEnd < pageCount - 2) pages.push(null) // trailing ellipsis

  // Always show last page
  pages.push(pageCount - 1)

  return pages
}

const ChevronLeft = () => (
  <svg fill='none' height='16' viewBox='0 0 16 16' width='16'>
    <path
      d='M10 12L6 8L10 4'
      stroke='currentColor'
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth='1.5'
    />
  </svg>
)

const ChevronRight = () => (
  <svg fill='none' height='16' viewBox='0 0 16 16' width='16'>
    <path
      d='M6 4L10 8L6 12'
      stroke='currentColor'
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth='1.5'
    />
  </svg>
)

export const TablePagination = ({
  total,
  pageIndex,
  pageSize,
  pageCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50],
  pageSizeLabel = 'Items per page:',
  centerControls = false,
}: TablePaginationProps) => {
  const colorScheme = useTableColorScheme()
  const density = useTableDensity()

  const firstItem = total === 0 ? 0 : pageIndex * pageSize + 1
  const lastItem = Math.min((pageIndex + 1) * pageSize, total)
  const rangeText = `${firstItem} - ${lastItem} of ${total}`

  const pageNumbers = getPageNumbers(pageIndex, pageCount)

  const isPrevDisabled = pageIndex === 0
  const isNextDisabled = pageIndex >= pageCount - 1

  return (
    <Box {...paginationStyles(colorScheme, density)}>
      {/* Range */}
      <Box
        {...paginationRangeStyles(colorScheme, density)}
        {...(centerControls && { flex: 1 })}
      >
        {rangeText}
      </Box>

      {/* Page controls */}
      <Box {...paginationControlsStyles(density)}>
        <chakra.button
          {...paginationNavButtonStyles(colorScheme, isPrevDisabled, density)}
          aria-label='Previous page'
          disabled={isPrevDisabled}
          onClick={() => !isPrevDisabled && onPageChange(pageIndex - 1)}
          type='button'
        >
          <ChevronLeft />
        </chakra.button>

        {pageNumbers.map((page, i) =>
          page === null ? (
            <Box
              key={`ellipsis-${i}`}
              {...paginationPageButtonStyles(colorScheme, false, density)}
              pointerEvents='none'
            >
              …
            </Box>
          ) : (
            <chakra.button
              key={page}
              {...paginationPageButtonStyles(
                colorScheme,
                page === pageIndex,
                density
              )}
              aria-current={page === pageIndex ? 'page' : undefined}
              aria-label={`Page ${page + 1}`}
              onClick={() => onPageChange(page)}
              type='button'
            >
              {page + 1}
            </chakra.button>
          )
        )}

        <chakra.button
          {...paginationNavButtonStyles(colorScheme, isNextDisabled, density)}
          aria-label='Next page'
          disabled={isNextDisabled}
          onClick={() => !isNextDisabled && onPageChange(pageIndex + 1)}
          type='button'
        >
          <ChevronRight />
        </chakra.button>
      </Box>

      {/* Per-page selector */}
      {onPageSizeChange && (
        <Box {...paginationPerPageStyles(density)}>
          <Box {...paginationPerPageLabelStyles(colorScheme, density)}>
            {pageSizeLabel}
          </Box>
          <chakra.select
            {...paginationPerPageSelectStyles(colorScheme, density)}
            aria-label={pageSizeLabel}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              onPageSizeChange(Number(e.target.value))
            }
            value={pageSize}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </chakra.select>
        </Box>
      )}

      {/* Balancing spacer: mirrors the range so the controls stay centered
          when there is no per-page selector. */}
      {centerControls && !onPageSizeChange && <Box flex={1} />}
    </Box>
  )
}
